import { QualityChoiceModal } from "@components/quality-choice-modal";
import { useLoras } from "@features/loras/use-loras";
import { useUser } from "@features/user/use-user";
import { useNavigateAlt } from "@features/utils";
import { CheckIcon, SparklesIcon } from "@heroicons/react/16/solid";
import { Button, Text } from "@radix-ui/themes";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

interface UpgradeToHighQualityButtonProps {
  size?: "1" | "2" | "3" | "4";
  variant?: "solid" | "soft" | "outline" | "ghost";
  className?: string;
  compact?: boolean;
}

export const UpgradeToHighQualityButton = ({
  size = "3",
  variant = "solid",
  className = "",
  compact = false,
}: UpgradeToHighQualityButtonProps) => {
  const { t } = useTranslation();
  const navigate = useNavigateAlt();
  const { site } = useParams();
  const { loras } = useLoras();
  const [showQualityModal, setShowQualityModal] = useState(false);
  const { credits } = useUser();

  // Find the first low-quality avatar model
  const lowQualityAvatar = loras.data?.find(
    (lora) =>
      lora.technology === "images" &&
      lora.subtype === "avatar" &&
      lora.status === "completed",
  );

  const hasCreditsToCreateHighQuality = credits > 400;

  // Don't show the button if there's no low-quality avatar
  if (loras.data?.length) {
    return null;
  }

  const handleUpgrade = (event: React.MouseEvent) => {
    setShowQualityModal(true);
  };

  const handleAddMorePictures = () => {
    navigate(
      lowQualityAvatar?.id
        ? `/${site || "portraits"}/lora/${lowQualityAvatar?.id}/upgrade`
        : `/${site || "portraits"}/lora/model`,
    );
    setShowQualityModal(false);
  };

  const handleUpgradeFromModal = () => {
    navigate(`/billing`);
    setShowQualityModal(false);
  };

  const handleContinueStandard = () => {
    setShowQualityModal(false);
  };

  const advantages = [
    t("quality_modal.premium_section.feature_1", "Better face likeness"),
    t("quality_modal.premium_section.feature_2", "More realistic results"),
    t("quality_modal.premium_section.feature_3", "Works with any style"),
  ];

  return (
    <>
      <div
        className={`relative overflow-hidden rounded-xl bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 p-[1px] ${className}`}
      >
        <div className="relative rounded-[11px] bg-white dark:bg-slate-900 p-4">
          {/* Header */}
          <div className="flex items-center gap-2 mb-3">
            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 shrink-0">
              <SparklesIcon className="w-4 h-4 text-white" />
            </div>
            <div>
              <Text weight="bold" size="2" className="block">
                {hasCreditsToCreateHighQuality
                  ? t(
                      "loras.upgrade_card.title_with_credits",
                      "Train your first high quality model",
                    )
                  : t(
                      "loras.upgrade_card.title",
                      "Remove watermark and get higher quality",
                    )}
              </Text>
              <Text size="1" color="gray">
                {t("loras.upgrade_card.subtitle", "Get better results")}
              </Text>
            </div>
          </div>

          {/* Advantages */}
          <div className="space-y-1.5 mb-4">
            {advantages.map((advantage, index) => (
              <div key={index} className="flex items-center gap-2">
                <div className="flex items-center justify-center w-4 h-4 rounded-full bg-green-100 dark:bg-green-900/30">
                  <CheckIcon className="w-2.5 h-2.5 text-green-600 dark:text-green-400" />
                </div>
                <Text size="1" color="gray">
                  {advantage}
                </Text>
              </div>
            ))}
          </div>

          {/* CTA Button */}
          <Button
            size="2"
            className="w-full hue-animation"
            onClick={handleUpgrade}
          >
            <SparklesIcon className="w-3.5 h-3.5" />
            {hasCreditsToCreateHighQuality
              ? t("loras.upgrade_card.button_with_credits", "Train my model")
              : t("loras.upgrade_card.button", "Upgrade Now")}
          </Button>
        </div>
      </div>

      <QualityChoiceModal
        isOpen={showQualityModal}
        onClose={() => setShowQualityModal(false)}
        onContinueStandard={handleContinueStandard}
        onAddMorePictures={handleAddMorePictures}
        onUpgrade={handleUpgradeFromModal}
      />
    </>
  );
};
